import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { isMobileUA, isNativeAppUA } from 'dpl/shared/utils';
import { BUYER_APP_STORE_URL } from 'dpl/shared/constants/urls';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import { postReactNativeCloseWebViewMessage } from 'dpl/native_app/utils';
import MessagePreview from 'dpl/common/components/MessagePreview';
import Badge from 'dpl/common/design_system/Badge';
import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';

import contactsPhoneWallpaper from 'dpl/assets/contacts_phone_wallpaper.png';

const IS_MOBILE_UA = isMobileUA();
const IS_NATIVE_UA = isNativeAppUA();
const APP_STORE_BUTTON_CTA = 'Continue in the app';

function getMessagePreviewSubtitle(dogName?: string) {
  return dogName
    ? `Thanks for reaching out about ${dogName}! Would you like to set up a phone call so I can share a bit more about my program?`
    : 'Thanks for reaching out! Would you like to set up a phone call so I can share a bit more about my program?';
}

function trackAppStoreButtonClicked() {
  sendRudderstackEvent(ANALYTICS_EVENTS.APP_PROMPT_INTERACTION_BY_USER, {
    button_cta: APP_STORE_BUTTON_CTA,
    view: ANALYTICS_VIEWS.CONTACT_SUCCESS_PAGE,
    view_feature: ANALYTICS_VIEW_FEATURES.APP_DOWNLOAD_PROMPT
  });
}

interface DesktopProps {
  breederDisplayName: string;
  breederFirstName: string;
  dogName?: string;
  breederProfilePhotoUrl?: string;
}

function Desktop({
  breederDisplayName,
  breederFirstName,
  dogName,
  breederProfilePhotoUrl
}: DesktopProps) {
  return (
    <div className="min-h-100 flex justify-center items-center BuyerProfileSuccess bg-stone-700 white">
      <div className="SectionLayout flex flex-column items-center pv4 ph4">
        <Badge variant="bright" color="strollBright">
          <Icon name="fetch-success-alt" height="16px" width="16px" />
          <span>Your request was sent</span>
        </Badge>
        <h4 className="mv8 w-60-md w-100 tc">
          Next, get the Good Dog app to keep chatting with {breederFirstName}.
        </h4>
        <div className="mb15">
          <MessagePreview
            title={breederDisplayName}
            subtitle={getMessagePreviewSubtitle(dogName)}
            coverPhotoUrl={breederProfilePhotoUrl}
          />
        </div>

        <QRCodeSVG
          style={{ border: '1px solid white' }}
          value={BUYER_APP_STORE_URL}
          size={90}
          fgColor="white"
          bgColor="#283E52"
        />
        <p className="o-90 mt4">Scan the QR Code</p>
      </div>
    </div>
  );
}

interface MobileProps {
  breederDisplayName: string;
  breederFirstName: string;
  breederProfilePhotoUrl?: string;
  dogName?: string;
}

function Mobile({
  breederDisplayName,
  breederFirstName,
  breederProfilePhotoUrl,
  dogName
}: MobileProps) {
  return (
    <div className="flex flex-column relative h-100 min-h-100 BuyerProfileSuccess">
      <div className="flex justify-center mv8">
        <Badge variant="bright" color="strollBright">
          <Icon name="fetch-success-alt" height="16px" width="16px" />
          <span>Request sent</span>
        </Badge>
      </div>

      <div className="BuyerProfileSuccess__phone-frame relative overflow-hidden bt bl br b--stone-700 mh8">
        <SmartImage
          className="w-100"
          src={contactsPhoneWallpaper}
          height="456px"
          crop
        />
        <h4 className="BuyerProfileSuccess__phone-frame-title absolute top-0 tc ph5 lh-title font-28">
          Next, get the app to keep chatting with {breederFirstName}.
        </h4>
        <div className="BuyerProfileSuccess__message-preview absolute left-0 right-0 padding-h-10">
          <MessagePreview
            title={breederDisplayName}
            subtitle={getMessagePreviewSubtitle(dogName)}
            coverPhotoUrl={breederProfilePhotoUrl}
          />
        </div>
      </div>
      <div className="bg-white bt b--default pv4 ph5 absolute bottom-0 w-100">
        <div className="flex mb3">
          <Icon
            className="bg-blue-500 br2"
            name="gooddogavatar"
            width="40px"
            height="40px"
          />
          <div className="ml3">
            <p className="fw-medium font-14 mb1 lh-normal">Good Dog app</p>
            <p className="font-12 tertiary">
              Fast messaging, safe transactions.
            </p>
          </div>
        </div>
        <a
          className="button button--primary fw-medium w-100 tc font-16"
          href={BUYER_APP_STORE_URL}
          onClick={trackAppStoreButtonClicked}
        >
          {APP_STORE_BUTTON_CTA}
        </a>
      </div>
    </div>
  );
}

interface NativeProps {
  breederFirstName: string;
}

function Native({ breederFirstName }: NativeProps) {
  return (
    <div className="min-h-100 flex justify-center items-center BuyerProfileSuccess">
      <div className="SectionLayout pv4 ph4 tc">
        <h6 className="mb4">Your request was sent!</h6>
        <div className="BuyerProfileSuccess__content font-16 mb10">
          <p>{breederFirstName} will get back to you soon.</p>
          <p>
            You’ll hear back from {breederFirstName} soon. While you wait, jump
            back into your search.
          </p>
        </div>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={postReactNativeCloseWebViewMessage}
            className="button bg-white b--light-gray max-content"
          >
            Continue searching
          </button>
        </div>
      </div>
    </div>
  );
}

interface BuyerProfileSuccessProps {
  breederDisplayName: string;
  breederFirstName: string;
  breederProfilePhotoUrl?: string;
  dogName?: string;
}

export default function BuyerProfileSuccess({
  breederDisplayName,
  breederFirstName,
  breederProfilePhotoUrl,
  dogName
}: BuyerProfileSuccessProps) {
  if (IS_NATIVE_UA) {
    return <Native breederFirstName={breederFirstName} />;
  }

  if (IS_MOBILE_UA) {
    return (
      <Mobile
        breederDisplayName={breederDisplayName}
        breederFirstName={breederFirstName}
        breederProfilePhotoUrl={breederProfilePhotoUrl}
        dogName={dogName}
      />
    );
  }

  return (
    <Desktop
      breederFirstName={breederFirstName}
      breederProfilePhotoUrl={breederProfilePhotoUrl}
      dogName={dogName}
      breederDisplayName={breederDisplayName}
    />
  );
}
